import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileText, User, Car, Shield, Camera, Mail, Phone, MapPin, Calendar, Edit, Trash2, Download, Loader2, AlertCircle, CheckCircle, Eye, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/Layout/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CaseDetailPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) { navigate('/'); return; }
    setUser(JSON.parse(userData));
    fetchCaseDetails();
    // eslint-disable-next-line
  }, [navigate, caseId]);

  const fetchCaseDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const [caseRes, photosRes] = await Promise.all([
        fetch(`${API_URL}/api/cases/${caseId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/cases/${caseId}/photos`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (!caseRes.ok) { setError(caseRes.status === 404 ? 'Akte nicht gefunden' : 'Fehler beim Laden'); setIsLoading(false); return; }
      setCaseData(await caseRes.json());
      if (photosRes.ok) setPhotos(await photosRes.json());
    } catch { setError('Verbindungsfehler'); }
    finally { setIsLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Akte wirklich löschen?')) return;
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/cases/${caseId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) navigate('/dashboard');
    } catch {} finally { setIsDeleting(false); }
  };

  const handleStatusChange = async (status) => {
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/cases/${caseId}/status`, {
      method: 'PATCH', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    setCaseData(prev => ({ ...prev, status }));
  };

  const badge = (status) => ({
    'Neu': 'bg-blue-100 text-blue-700', 'In Bearbeitung': 'bg-amber-100 text-amber-700',
    'Abgeschlossen': 'bg-emerald-100 text-emerald-700', 'Abgerechnet': 'bg-violet-100 text-violet-700'
  }[status] || 'bg-gray-100 text-gray-700');

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  if (!user) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (isLoading) return <DashboardLayout user={user}><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-10 h-10 text-blue-500 animate-spin" /></div></DashboardLayout>;
  if (error) return <DashboardLayout user={user}><div className="flex items-center justify-center min-h-[60vh]"><div className="text-center"><AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" /><p className="text-gray-900 font-medium mb-2">{error}</p><Button onClick={() => navigate('/dashboard')} variant="outline">Zurück</Button></div></div></DashboardLayout>;

  const InfoRow = ({ label, value }) => (
    <div className="flex justify-between py-1.5 border-b border-gray-50">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value || '-'}</span>
    </div>
  );

  return (
    <DashboardLayout user={user}>
      <div className="p-4 lg:p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-gray-600" /></Link>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-gray-900">
                  {caseData.vorname && caseData.nachname ? `${caseData.vorname} ${caseData.nachname}` : 'Neue Akte'}
                </h1>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge(caseData.status)}`}>{caseData.status}</span>
              </div>
              <p className="text-gray-500 text-sm mt-0.5">#{caseData.id.slice(0, 8).toUpperCase()} | {formatDate(caseData.created_at)}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={caseData.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="h-9 px-3 border border-gray-200 rounded-lg text-sm bg-white"
            >
              <option value="Neu">Neu</option>
              <option value="In Bearbeitung">In Bearbeitung</option>
              <option value="Abgeschlossen">Abgeschlossen</option>
              <option value="Abgerechnet">Abgerechnet</option>
            </select>
            <Button variant="outline" className="gap-2 h-9" onClick={() => navigate(`/neuer-fall?edit=${caseData.id}`)}>
              <Edit className="w-4 h-4" /> Bearbeiten
            </Button>
            <Button variant="outline" className="gap-2 h-9 text-red-600 hover:bg-red-50" onClick={handleDelete} disabled={isDeleting}>
              <Trash2 className="w-4 h-4" /> {isDeleting ? 'Löscht...' : 'Löschen'}
            </Button>
          </div>
        </div>

        {/* Empty case banner */}
        {!caseData.vorname && !caseData.nachname && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Edit className="w-5 h-5 text-amber-600" />
              <p className="text-sm text-amber-800">Diese Akte ist noch leer.</p>
            </div>
            <Button onClick={() => navigate(`/neuer-fall?edit=${caseData.id}`)} className="bg-[#29beff] hover:bg-[#1fb3f5] text-white text-sm gap-2 h-8 px-4">
              <Edit className="w-4 h-4" /> Daten erfassen
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Kundendaten */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><User className="w-4 h-4 text-gray-400" /> Kundendaten</h2>
              <InfoRow label="Name" value={`${caseData.vorname || ''} ${caseData.nachname || ''}`.trim()} />
              <InfoRow label="Adresse" value={caseData.adresse} />
              <InfoRow label="PLZ/Ort" value={caseData.plzOrt} />
              <InfoRow label="Telefon" value={caseData.telefon} />
              <InfoRow label="E-Mail" value={caseData.email} />
            </div>

            {/* Fahrzeugdaten */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><Car className="w-4 h-4 text-gray-400" /> Fahrzeugdaten</h2>
              <InfoRow label="Fahrzeugtyp" value={caseData.fahrzeugtyp} />
              <InfoRow label="Marke / Modell" value={`${caseData.marke || ''} ${caseData.modell || ''}`.trim()} />
              <InfoRow label="Kennzeichen" value={caseData.kennzeichen} />
              <InfoRow label="VIN" value={caseData.vin} />
              <InfoRow label="Baujahr" value={caseData.baujahr} />
              <InfoRow label="Erstzulassung" value={caseData.erstzulassung} />
              <InfoRow label="Laufleistung" value={caseData.laufleistung ? `${parseInt(caseData.laufleistung).toLocaleString('de-DE')} km` : ''} />
              <InfoRow label="Versicherung" value={caseData.versicherung} />
            </div>

            {/* Lichtbilder */}
            {photos.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><Camera className="w-4 h-4 text-gray-400" /> Lichtbilder ({photos.length})</h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {photos.map((photo) => (
                    <button
                      key={photo.id}
                      onClick={() => setPreviewPhoto(photo)}
                      className="aspect-square rounded-lg border border-gray-200 overflow-hidden hover:border-[#29beff] hover:shadow-md transition-all"
                    >
                      <img src={`${API_URL}${photo.url}`} alt={photo.photo_type} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* Status */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Status</h2>
              <div className="space-y-2">
                <InfoRow label="Status" value={caseData.status} />
                <InfoRow label="Erstellt" value={formatDate(caseData.created_at)} />
                <InfoRow label="Auftragstyp" value={caseData.auftragstyp} />
                <InfoRow label="Vorsteuer" value={caseData.vorsteuerabzugsberechtigt} />
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Aktionen</h2>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start gap-2 h-9 text-sm" onClick={() => navigate(`/neuer-fall?edit=${caseData.id}`)}>
                  <Edit className="w-4 h-4" /> Akte bearbeiten
                </Button>
                <Button variant="outline" className="w-full justify-start gap-2 h-9 text-sm">
                  <Download className="w-4 h-4" /> Gutachten exportieren
                </Button>
                <Button variant="outline" className="w-full justify-start gap-2 h-9 text-sm">
                  <Mail className="w-4 h-4" /> E-Mail senden
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Photo Preview Modal */}
      {previewPhoto && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreviewPhoto(null)}>
          <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700">{previewPhoto.photo_type}</span>
              <button onClick={() => setPreviewPhoto(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-2 overflow-auto max-h-[calc(90vh-52px)]">
              <img src={`${API_URL}${previewPhoto.url}`} alt={previewPhoto.photo_type} className="w-full object-contain" />
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CaseDetailPage;
